import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Chip, CircularProgress, IconButton, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';

import { GET_ROUTE_AUTOMATIC_EXPIRATION_PROMOTION_SETTING_FORMS } from '../../../../../core/constants';
import { getAutomaticExpirationPromotionSettingsNotificationData, updateAutomaticExpirationPromotionSettingStatusActionNotificationData } from '../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../core/enums/common/api';
import useNotification from '../../../../../core/hooks/use_notification';
import { getAutomaticExpirationPromotionSettingsAction, getAutomaticExpirationPromotionSettingsState } from '../../../../../features/price/get_automatic_expiration_promotion_settings';
import { getAutomaticExpirationPromotionSettingType } from '../../../../common/utils/automatic_expiration_promotion_setting_utils';
import { updateAutomaticExpirationPromotionSettingStatusAction } from '../../../../../features/price/update_automatic_expiration_promotion_setting_status';

const AutomaticExpirationPromotionSettingList = ({ tab }) => {
    const { showErrorMessage, showNotification } = useNotification();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const getAutomaticExpirationPromotionSettings = useSelector(getAutomaticExpirationPromotionSettingsState);

    const [updatingStatusIds, setUpdatingStatusIds] = useState({});

    const refreshAutomaticExpirationPromotionSettingList = useCallback(() => {
        dispatch(getAutomaticExpirationPromotionSettingsAction())
            .then(response => showErrorMessage(getAutomaticExpirationPromotionSettingsNotificationData(response)));
    }, [dispatch, showErrorMessage]);

    useEffect(() => {
        refreshAutomaticExpirationPromotionSettingList();
    }, [refreshAutomaticExpirationPromotionSettingList]);

    const onUpdateAutomaticExpirationPromotionSettingStatus = (id) => {
        setUpdatingStatusIds(prev => ({ ...prev, [id]: true }));

        dispatch(updateAutomaticExpirationPromotionSettingStatusAction(id))
            .then(response => {
                showNotification(updateAutomaticExpirationPromotionSettingStatusActionNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshAutomaticExpirationPromotionSettingList();
            })
            .finally(() => {
                setUpdatingStatusIds(prev => ({ ...prev, [id]: false }));
            });
    };

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', gap: 2 } }>
            { getAutomaticExpirationPromotionSettings?.loading && <LinearProgress/> }

            {
                getAutomaticExpirationPromotionSettings?.data &&
                <TableContainer component={ Paper } variant="outlined">
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                <TableCell>Typ promocji</TableCell>
                                <TableCell>Nazwa promocji</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell align="right" sx={ { pr: 3 } }></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                getAutomaticExpirationPromotionSettings?.data?.map(setting => {
                                    const isUpdating = updatingStatusIds[setting?.id] || false;

                                    return (
                                        <TableRow key={ setting?.id } hover>
                                            <TableCell>
                                                { getAutomaticExpirationPromotionSettingType(setting?.type) }
                                            </TableCell>
                                            <TableCell sx={ { fontWeight: 500 } }>
                                                { setting?.name }
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={ setting?.active ? 'Aktywny' : 'Nieaktywny' }
                                                    color={ setting?.active ? 'success' : 'default' }
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right" sx={ { pr: 2 } }>
                                                <Stack direction="row" spacing={ 1 } justifyContent="flex-end"
                                                       alignItems="center">
                                                    <Tooltip title={ setting?.active ? 'Dezaktywuj' : 'Aktywuj' }>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color={ setting?.active ? 'error' : 'success' }
                                                                onClick={ () => onUpdateAutomaticExpirationPromotionSettingStatus(setting?.id) }
                                                                disabled={ isUpdating }
                                                            >
                                                                { isUpdating ?
                                                                    <CircularProgress size={ 20 } color="inherit"/> :
                                                                    <PowerSettingsNewIcon fontSize="small"/> }
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>

                                                    <Tooltip title="Edytuj zasady">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={ () => navigate(GET_ROUTE_AUTOMATIC_EXPIRATION_PROMOTION_SETTING_FORMS(setting?.id), { state: { tab } }) }
                                                        >
                                                            <EditIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            }
        </Box>
    );
};

export default AutomaticExpirationPromotionSettingList;